import { CreditCardOutlined, NumberOutlined, ReloadOutlined, ScanOutlined } from '@ant-design/icons';
import { DatePicker, Radio, theme } from 'antd';
import dayjs from 'dayjs';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectCardTemplatesIR } from 'reduxStore/types/selectors';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { CONSTANTS_SETTINGS as SETTINGS } from 'config/settingsContainer';
import { Button, Flex, Form, Input, Modal, Segmented, Select, Tag, Tooltip, Typography } from 'zui/Antd';
import { SelectFontSizeLG } from 'zui/Antd/Select';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const generatePasscode = (length) => {
	const first = String(Math.floor(Math.random() * 9) + 1);
	const rest = Array.from({ length: length - 1 }, () => Math.floor(Math.random() * 10)).join('');
	return first + rest;
};

const AddCredentialModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	const {
		token: {
			paddingSM,
			paddingMD,
			paddingLG,
			fontSizeSM,
			colorTextDescription,
			colorPrimary,
			colorPrimaryBg,
			colorFillTertiary,
			colorBorderSecondary,
			colorBorder,
			paddingXL,
			borderRadiusLG
		}
	} = theme.useToken();

	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const { loadingCreateCredential } = useSelector((state) => state.Credentials);
	const cardTemplatesIR = useSelector(selectCardTemplatesIR);
	const orgSettings = useSelector(selectCurrentProfileOrgSettings);

	const passcodeLength = Math.min(
		9,
		Math.max(
			4,
			Number(
				orgSettings?.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CONTROL_PASSCODE_LENGTH)?.value
			) || 6
		)
	);

	const isIRActive = Boolean(
		Number(orgSettings?.find((s) => s.defaultSettingID === SETTINGS.INNER_RANGE_MODULE)?.value)
	);

	const isControlActive = Boolean(
		Number(orgSettings?.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CONTROL_MODULE)?.value)
	);

	const isCSNRequired = isControlActive;

	const defaultTemplate = useMemo(() => {
		const wiegand = cardTemplatesIR.find((t) => t.addressID === '121');
		return wiegand || cardTemplatesIR[0] || null;
	}, [cardTemplatesIR]);

	// Shared state
	const [credentialType, setCredentialType] = useState('card');
	const [name, setName] = useState('');
	const [internalVisible, setInternalVisible] = useState(false);
	const [showErrors, setShowErrors] = useState(false);
	const csnInputRef = useRef(null);

	// Card state
	const [cardNumber, setCardNumber] = useState('');
	const [cardSerialNumber, setCardSerialNumber] = useState('');
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	// Passcode state
	const [passcode, setPasscode] = useState('');

	// Validity state
	const [validityType, setValidityType] = useState('permanent');
	const [dateRange, setDateRange] = useState(null);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;
	const canAddCredential = useManagePermittedRoles('addCredential');
	const canManageSiteAssignment = useManagePermittedRoles('manageSiteAssignment');

	// Site selection
	const sites = useSelector(selectSites);
	const [selectedSiteIDs, setSelectedSiteIDs] = useState([]);

	// For non-global admin, default to the first available site
	useEffect(() => {
		if (visible && !canManageSiteAssignment && sites.length > 0 && selectedSiteIDs.length === 0) {
			setSelectedSiteIDs([sites[0].siteID]);
		}
	}, [visible, canManageSiteAssignment, sites, selectedSiteIDs.length]);

	const isCard = credentialType === 'card';

	// Auto-select default template when modal opens
	useEffect(() => {
		if (visible && !selectedTemplate && defaultTemplate) {
			setSelectedTemplate(defaultTemplate);
		}
	}, [visible, defaultTemplate, selectedTemplate]);

	// Card reader — global keydown listener for CSN auto-fill
	useEffect(() => {
		if (!visible || !isCard) return;
		let buffer = '';
		let timeout;
		const handleKeyDown = (e) => {
			clearTimeout(timeout);
			if (e.key === 'Enter') {
				if (buffer.length > 4) setCardSerialNumber(buffer);
				buffer = '';
				return;
			}
			if (e.key.length === 1) buffer += e.key;
			timeout = setTimeout(() => {
				buffer = '';
			}, 50);
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			clearTimeout(timeout);
		};
	}, [visible, isCard]);

	const handleGenerate = useCallback(() => {
		setPasscode(generatePasscode(passcodeLength));
	}, [passcodeLength]);

	const handleCancel = () => {
		form.resetFields();
		setName('');
		setCardNumber('');
		setCardSerialNumber('');
		setSelectedTemplate(defaultTemplate);
		setPasscode('');
		setValidityType('permanent');
		setDateRange(null);
		setSelectedSiteIDs([]);
		setShowErrors(false);
		setVisible(false);
	};

	const isValidityInvalid = validityType === 'time-based' && (!dateRange || dateRange.length !== 2);

	const isSiteInvalid = !canManageSiteAssignment && sites.length > 0 && selectedSiteIDs.length === 0;

	const isSubmitDisabled = isCard
		? loadingCreateCredential ||
			!name?.trim() ||
			(isIRActive ? !cardNumber || !selectedTemplate : false) ||
			(isCSNRequired && !cardSerialNumber) ||
			isValidityInvalid ||
			isSiteInvalid
		: loadingCreateCredential || !name || !passcode || passcode.length !== passcodeLength || isValidityInvalid || isSiteInvalid;

	const validityPayload = validityType === 'permanent'
		? { startDate: null, endDate: null }
		: { startDate: dateRange?.[0]?.toISOString() || null, endDate: dateRange?.[1]?.toISOString() || null };

	const handleSubmit = () => {
		setShowErrors(true);
		if (!canAddCredential || isSubmitDisabled) return;

		if (isCard) {
			dispatch(
				credentialsActions.createCredential({
					data: {
						name,
						value: isIRActive ? cardNumber : cardSerialNumber,
						uniqueIdentifier: cardSerialNumber || null,
						credentialTypeID: 1,
						externalCredentialTemplateID: selectedTemplate?.credentialTemplateID || null,
						siteIDs: selectedSiteIDs,
						...validityPayload
					}
				})
			);
		} else {
			dispatch(
				credentialsActions.createCredential({
					data: {
						name,
						value: passcode,
						credentialTypeID: 2,
						siteIDs: selectedSiteIDs,
						...validityPayload
					}
				})
			);
		}

		handleCancel();
	};

	const headerIcon = isCard ? (
		<CreditCardOutlined style={{ fontSize: 20, color: colorPrimary }} />
	) : (
		<NumberOutlined style={{ fontSize: 20, color: colorPrimary }} />
	);

	const subtitle = isCard
		? 'Create a card credential. Assign it to a person later to propagate to locks.'
		: 'Create a passcode credential. Assign it to a person later to propagate to locks.';

	return (
		<>
			<Tooltip
				title={!canAddCredential ? "You don't have permission to perform this action." : undefined}
				placement='top'
			>
				<span onClick={() => canAddCredential && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canAddCredential}>
							Add Credential
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				width={480}
				centered
				closeIcon={false}
				destroyOnClose
				styles={{
					content: { padding: 0, overflow: 'hidden' },
					body: { padding: `${paddingLG}px ${paddingXL}px`, maxHeight: '80vh', overflowY: 'auto' },
					footer: { padding: `${paddingMD}px ${paddingXL}px`, borderTop: `1px solid ${colorBorderSecondary}` }
				}}
				footer={
					<Flex justify='flex-end' gap={paddingSM}>
						<Button onClick={handleCancel} disabled={loadingCreateCredential}>
							Cancel
						</Button>
						<Button
							type='primary'
							onClick={handleSubmit}
							disabled={isSubmitDisabled}
							loading={loadingCreateCredential}
						>
							Add Credential
						</Button>
					</Flex>
				}
			>
				{/* Header */}
				<Flex align='flex-start' gap={paddingMD} style={{ marginBottom: paddingLG }}>
					<Flex
						align='center'
						justify='center'
						style={{
							width: 44,
							height: 44,
							background: colorPrimaryBg,
							borderRadius: borderRadiusLG,
							flexShrink: 0
						}}
					>
						{headerIcon}
					</Flex>
					<Flex vertical gap={2}>
						<Title level={4} style={{ margin: 0 }}>
							Add Credential
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>{subtitle}</Text>
					</Flex>
				</Flex>

				<Form layout='vertical' form={form} requiredMark={false}>
					{/* Type selector */}
					<Form.Item style={{ marginBottom: paddingMD }}>
						<Segmented
							block
							value={credentialType}
							onChange={(val) => {
								setCredentialType(val);
								setShowErrors(false);
							}}
							options={[
								{ label: 'Card', value: 'card', icon: <CreditCardOutlined /> },
								{ label: 'Passcode', value: 'passcode', icon: <NumberOutlined /> }
							]}
						/>
					</Form.Item>

					{/* Name (shared) */}
					<Form.Item
						label='Name'
						required
						validateStatus={showErrors && !name?.trim() ? 'error' : ''}
						help={showErrors && !name?.trim() ? 'Name is required' : ''}
					>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={isCard ? 'e.g. Main Entrance Card' : 'e.g. John Smith, Reception Code'}
							onPressEnter={() => isCard && csnInputRef.current?.focus()}
							onKeyDown={(e) => {
								if (e.key === 'Tab' && isCard && !e.shiftKey) {
									e.preventDefault();
									csnInputRef.current?.focus();
								}
							}}
						/>
					</Form.Item>

					{/* Site assignment */}
					{sites.length > 0 && (
						<Form.Item
							label='Select Site'
							required={!canManageSiteAssignment}
							validateStatus={showErrors && isSiteInvalid ? 'error' : ''}
							help={showErrors && isSiteInvalid ? 'At least one site is required' : ''}
							extra={
								!canManageSiteAssignment && sites.length <= 1 && (
									<Text type='secondary' style={{ fontSize: 12 }}>
										Credential will automatically be added to your assigned site.
									</Text>
								)
							}
						>
							{canManageSiteAssignment ? (
								<SelectFontSizeLG
									mode='multiple'
									value={selectedSiteIDs}
									onChange={setSelectedSiteIDs}
									placeholder='Select Sites'
									options={sites.map((site) => ({
										label: site.name,
										value: site.siteID
									}))}
									showSearch={false}
									virtual={false}
									maxTagCount={2}
									maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
									$tagColor={colorPrimary}
									style={{ width: '100%' }}
									allowClear
								/>
							) : (
								<Select
									value={selectedSiteIDs[0]}
									disabled={sites.length <= 1}
									onChange={(value) => setSelectedSiteIDs([value])}
									options={sites.map((site) => ({
										label: site.name,
										value: site.siteID
									}))}
									style={{ width: '100%' }}
								/>
							)}
						</Form.Item>
					)}

					{/* 📍 VALIDITY PERIOD */}
					<Form.Item label='Access Validity' style={{ marginBottom: paddingMD }}>
						<Radio.Group
							value={validityType}
							onChange={(e) => setValidityType(e.target.value)}
						>
							<Radio value='permanent'>Permanent</Radio>
							<Radio value='time-based'>Time-based</Radio>
						</Radio.Group>
					</Form.Item>

					{validityType === 'time-based' && (
						<Form.Item
							label='Period'
							required
							validateStatus={showErrors && (!dateRange || dateRange.length !== 2) ? 'error' : ''}
							help={showErrors && (!dateRange || dateRange.length !== 2) ? 'Please select a date range' : ''}
						>
							<RangePicker
								value={dateRange}
								onChange={setDateRange}
								format='DD/MM/YYYY hh:mm A'
								showTime
								style={{ width: '100%' }}
								allowClear={false}
								defaultValue={[dayjs(), dayjs().add(30, 'day')]}
								disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
								disabledTime={(current) => {
									if (!current || !current.isSame(dayjs(), 'day')) return {};
									const now = dayjs();
									return {
										disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
										disabledMinutes: (hour) =>
											hour === now.hour()
												? Array.from({ length: now.minute() }, (_, i) => i)
												: []
									};
								}}
							/>
						</Form.Item>
					)}

					{isCard ? (
						<>
							{/* IR active: Card Number + Site Code */}
							{isIRActive && (
								<Flex gap={paddingSM}>
									<Form.Item
										label='Card Number'
										required
										style={{ flex: 1 }}
										validateStatus={showErrors && !cardNumber ? 'error' : ''}
										help={showErrors && !cardNumber ? 'Card number is required' : ''}
									>
										<Input
											value={cardNumber}
											onChange={(e) => {
												const val = e.target.value.replace(/[^0-9]/g, '');
												if (val.length <= 8) setCardNumber(val);
											}}
											maxLength={8}
											placeholder='e.g. 0423'
										/>
									</Form.Item>
									<Form.Item
										label='Site Code'
										required
										style={{ flex: 1 }}
										validateStatus={showErrors && !selectedTemplate ? 'error' : ''}
										help={showErrors && !selectedTemplate ? 'Site code is required' : ''}
									>
										<Select
											value={selectedTemplate ? JSON.stringify(selectedTemplate) : undefined}
											options={cardTemplatesIR.map((t) => ({
												value: JSON.stringify(t),
												label: t.name || t.siteCode
											}))}
											onChange={(val) => setSelectedTemplate(JSON.parse(val))}
											placeholder='Select site code'
										/>
									</Form.Item>
								</Flex>
							)}

							{/* CSN — required if Control only, optional if IR active */}
							<div
								style={{
									padding: paddingMD,
									background: colorFillTertiary,
									borderRadius: borderRadiusLG,
									border: `1px dashed ${cardSerialNumber ? colorPrimary : colorBorder}`
								}}
							>
								<Flex align='center' justify='space-between' style={{ marginBottom: 8 }}>
									<Flex align='center' gap={6}>
										<ScanOutlined style={{ color: colorPrimary, fontSize: 14 }} />
										<Text strong style={{ fontSize: fontSizeSM }}>
											Card Serial Number (CSN)
										</Text>
									</Flex>
									{!isCSNRequired && (
										<Tag color='default' style={{ margin: 0, fontSize: 11 }}>
											optional
										</Tag>
									)}
								</Flex>
								<Input
									ref={csnInputRef}
									value={cardSerialNumber}
									onChange={(e) => setCardSerialNumber(e.target.value)}
									placeholder='Tap card on reader or enter manually...'
									status={showErrors && isCSNRequired && !cardSerialNumber ? 'error' : ''}
									suffix={
										<Tooltip title='Connect a USB card reader and tap the card — the CSN will fill in automatically.'>
											<Text type='secondary' style={{ fontSize: 12, cursor: 'default' }}>
												?
											</Text>
										</Tooltip>
									}
								/>
								{showErrors && isCSNRequired && !cardSerialNumber && (
									<Text type='danger' style={{ fontSize: 12 }}>
										CSN is required
									</Text>
								)}
							</div>
						</>
					) : (
						<>
							{/* Passcode */}
							<Form.Item
								label='Passcode'
								required
								style={{ marginBottom: 0 }}
								validateStatus={
									showErrors && (!passcode || passcode.length !== passcodeLength) ? 'error' : ''
								}
								help={
									showErrors && (!passcode || passcode.length !== passcodeLength)
										? `Passcode must be ${passcodeLength} digits`
										: ''
								}
							>
								<Input
									value={passcode}
									onChange={(e) =>
										setPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, passcodeLength))
									}
									placeholder={`${passcodeLength}-digit code`}
									maxLength={passcodeLength}
									addonAfter={
										<span
											onClick={handleGenerate}
											style={{
												cursor: 'pointer',
												color: colorPrimary,
												display: 'flex',
												alignItems: 'center',
												gap: 4,
												fontSize: fontSizeSM,
												userSelect: 'none'
											}}
										>
											<ReloadOutlined />
											Generate
										</span>
									}
								/>
							</Form.Item>
						</>
					)}
				</Form>
			</Modal>
		</>
	);
};

export default AddCredentialModal;
